<script setup lang="ts">
  import { IconImage, SimpleLoading, Tooltip } from '@/shared/ui';
  import { Can } from '@/entities/Permission';
  import Eye from '@/shared/assets/icons/EyeGray.svg';
  import { TRAINING_EMPTY_VALUE } from '../lib/trainingEmptyValue';

  export interface TrainingDocumentRow {
    id: number;
    code: string | null;
    name: string | null;
    revision?: string;
  }

  withDefaults(
    defineProps<{
      documents: TrainingDocumentRow[];
      loading?: boolean;
      emptyMessage?: string;
      showViewAction?: boolean;
      showRevision?: boolean;
    }>(),
    {
      emptyMessage: 'Nenhum documento encontrado para o cargo e revisão selecionados',
      showViewAction: true,
      showRevision: false,
    }
  );

  const emit = defineEmits<{
    view: [documentRevisionId: number];
  }>();
</script>

<template>
  <div
    data-testid="training-documents-table"
    class="flex flex-col overflow-hidden rounded-2xl border border-gray-02 bg-off-white"
  >
    <div class="flex items-center gap-4 border-b border-gray-02 px-6 py-3.5">
      <span class="w-30 text-sm font-semibold text-gray-07">Prefixo</span>
      <span class="text-sm font-semibold text-gray-07">Título</span>
      <span
        v-if="showRevision"
        class="w-30 text-sm font-semibold text-gray-07"
      >Revisão</span>
    </div>
    <div
      v-if="loading"
      data-testid="training-documents-loading"
      class="flex justify-center px-6 py-6"
    >
      <SimpleLoading />
    </div>
    <template v-else>
      <div
        v-for="document in documents"
        :key="document.id"
        data-testid="training-document-row"
        class="flex items-center justify-between gap-4 border-b border-gray-02 px-6 py-3.5 last:border-b-0"
      >
        <div class="flex items-center gap-4">
          <span class="w-30 text-sm text-gray-07">{{ document.code ?? TRAINING_EMPTY_VALUE }}</span>
          <span class="text-sm text-gray-07">{{ document.name ?? TRAINING_EMPTY_VALUE }}</span>
          <span
            v-if="showRevision"
            class="w-30 text-sm text-gray-07"
          >{{ document.revision ?? TRAINING_EMPTY_VALUE }}</span>
        </div>
        <Can
          v-if="showViewAction"
          :permissions="[{ action: 'manage', subject: 'DocumentManagement' }]"
        >
          <Tooltip content="Visualizar">
            <button
              type="button"
              aria-label="Visualizar documento"
              class="flex items-center justify-center"
              @click="emit('view', document.id)"
            >
              <IconImage
                :src="Eye"
                alt="Ícone de Visualização"
              />
            </button>
          </Tooltip>
        </Can>
      </div>
      <p
        v-if="documents.length === 0 && emptyMessage"
        data-testid="training-documents-empty"
        class="px-6 py-6 text-center text-sm text-gray-05"
      >
        {{ emptyMessage }}
      </p>
    </template>
  </div>
</template>
